import { cn } from '@sistemas/ui'
import { ChevronRight } from 'lucide-react'
import * as React from 'react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { NavLink, pinControls } from '@/components/sidebar-nav-link'
import { pinnedNavItems, type NavPins } from '@/lib/nav-pins'
import { groupForPath, navItemForPath, type NavGroup, type NavItem } from '@/lib/navigation'

// O arrasto dos fixados (dnd-kit) vem sob demanda, fora do JS inicial.
const SidebarPinnedSortable = React.lazy(() => import('@/components/sidebar-pinned-sortable'))

/**
 * Grupos da sidebar em acordeão: só o grupo da página atual fica aberto.
 *
 * A pessoa pode abrir outro grupo para espiar, mas isso não é preferência —
 * a próxima navegação volta ao estado "só o grupo atual". Persistir grupos
 * abertos traria de volta a sidebar comprida que motivou a mudança.
 *
 * Recolhida (64px) não há o que retrair: os ícones ficam em fila, com um
 * divisor entre grupos, e o `title` de cada item diz o nome.
 *
 * Itens fixados (pin) sobem para o topo, junto do Dashboard, na ordem em que
 * a pessoa escolheu (arrastando, ou Alt+↑/↓); continuam também no grupo deles,
 * com o pin marcado. Abrir um fixado NÃO mexe no acordeão: a página atual fica
 * marcada só no topo, e embaixo apenas o nome do grupo acende (pedido do dono,
 * 30/09 — a zona de baixo pulando a cada clique no topo incomodava).
 */
export function SidebarNav({
  groups,
  pathname,
  collapsed,
  pins,
}: {
  groups: readonly NavGroup[]
  pathname: string
  collapsed: boolean
  pins?: NavPins
}) {
  const current = groupForPath(pathname)
  const pinned = pins ? pinnedNavItems(groups, pins.pins) : []
  const pinnedSet = new Set(pinned.map((i) => i.to))
  const currentItem = navItemForPath(pathname)
  const onPinnedPage = currentItem !== null && pinnedSet.has(currentItem)
  const [open, setOpen] = React.useState<ReadonlySet<string>>(() =>
    onPinnedPage ? openOnly(null) : openOnly(current),
  )
  // Ajuste durante o render (e não em efeito): trocar de rota fecha os outros
  // grupos no mesmo quadro em que a página muda, sem piscar o estado antigo.
  // Página fixada deixa o acordeão como está.
  const [lastPath, setLastPath] = React.useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    if (!onPinnedPage) setOpen(openOnly(current))
  }

  const toggle = (id: string, next: boolean) =>
    setOpen((prev) => {
      const copy = new Set(prev)
      if (next) copy.add(id)
      else copy.delete(id)
      return copy
    })

  if (collapsed) {
    return (
      <div className="flex flex-col">
        {groups.map((group, index) => (
          <ul
            key={group.id}
            aria-label={group.label}
            className={cn(
              'flex flex-col gap-0.5',
              index > 0 && 'mt-2 border-t border-sidebar-border pt-2',
            )}
          >
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                item={item}
                pathname={pathname}
                collapsed
                suppressActive={pinnedSet.has(item.to)}
              />
            ))}
            {group.pinned &&
              pinned.map((item) => <NavLink key={`fixado:${item.to}`} item={item} pathname={pathname} collapsed />)}
          </ul>
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      {groups.map((group) =>
        group.pinned ? (
          <ul key={group.id} aria-label={group.label} className="mb-1 flex flex-col gap-0.5">
            {group.items.map((item) => (
              <NavLink key={item.to} item={item} pathname={pathname} />
            ))}
            {pins && pinned.length > 0 && (
              <React.Suspense fallback={<StaticPins items={pinned} pathname={pathname} pins={pins} />}>
                {pinned.length > 1 ? (
                  <SidebarPinnedSortable items={pinned} pathname={pathname} pins={pins} />
                ) : (
                  <StaticPins items={pinned} pathname={pathname} pins={pins} />
                )}
              </React.Suspense>
            )}
          </ul>
        ) : (
          <Collapsible
            key={group.id}
            open={open.has(group.id)}
            onOpenChange={(next) => toggle(group.id, next)}
          >
            <CollapsibleTrigger
              className={cn(
                'group flex h-7 w-full items-center gap-1.5 rounded-md px-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                group.id === current ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              <ChevronRight
                aria-hidden
                className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90"
              />
              <span className="flex-1 truncate text-left">{group.label}</span>
              <span className="tabular-nums tracking-normal opacity-60">
                {group.items.length}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="flex flex-col gap-0.5 pb-1 pt-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    item={item}
                    pathname={pathname}
                    suppressActive={pinnedSet.has(item.to)}
                    {...(pins
                      ? { pin: { pinned: pinnedSet.has(item.to), onToggle: () => pins.toggle(item.to) } }
                      : {})}
                  />
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        ),
      )}
    </div>
  )
}

function openOnly(id: string | null): ReadonlySet<string> {
  return new Set(id ? [id] : [])
}

/** Fixados sem arrasto: um só (não há o que ordenar) ou enquanto o dnd-kit carrega. */
function StaticPins({
  items,
  pathname,
  pins,
}: {
  items: readonly NavItem[]
  pathname: string
  pins: NavPins
}) {
  return items.map((item, index) => (
    <NavLink
      key={`fixado:${item.to}`}
      item={item}
      pathname={pathname}
      pin={pinControls(item.to, pins, index, items.length)}
    />
  ))
}
